import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useBusiness } from "@/hooks/use-business";
import { useFoodOrders } from "@/hooks/use-food-orders";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, Loader2, Mail, MapPin, Phone, User } from "lucide-react";
import { formatPhoneForDisplay } from "@/lib/phone-utils";
import { sendOwnerPush } from "@/lib/owner-push";

function money(currency: string, amount: number) {
  return `${currency} ${Number(amount || 0).toFixed(2)}`;
}

function addonsTotalPerUnit(it: any) {
  const addons = (it as any)?.addons || [];
  if (!Array.isArray(addons) || addons.length === 0) return 0;
  return addons.reduce(
    (s: number, a: any) => s + (Number(a.price_amount || 0) * Math.max(0, Number(a.quantity || 0))),
    0,
  );
}

function mapsUrl(args: { placeId?: string | null; address?: string | null; lat?: any; lng?: any }) {
  const placeId = args.placeId ? String(args.placeId) : "";
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  const lat = args.lat != null ? Number(args.lat) : null;
  const lng = args.lng != null ? Number(args.lng) : null;
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  const address = args.address ? String(args.address) : "";
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "";
}

function titleCase(input: string) {
  return input
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusLabel(raw: string) {
  return titleCase(String(raw || "new").replaceAll("_", " "));
}

function StatusChip({ status }: { status: string }) {
  const s = String(status || "new");
  const label = statusLabel(s);
  const cls =
    s === "new"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : s === "preparing"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : s === "ready"
          ? "bg-violet-50 text-violet-700 border-violet-200"
          : s === "out_for_delivery"
            ? "bg-sky-50 text-sky-700 border-sky-200"
            : s === "delivered"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : s === "not_delivered"
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : s === "cancelled"
                  ? "bg-slate-50 text-slate-600 border-slate-200"
                  : "bg-muted text-muted-foreground border-border";
  return (
    <Badge variant="outline" className={`whitespace-nowrap ${cls}`}>
      {label}
    </Badge>
  );
}

function FulfillmentChip({ isDelivery }: { isDelivery: boolean }) {
  return (
    <Badge
      variant="outline"
      className={`whitespace-nowrap ${
        isDelivery ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"
      }`}
    >
      {isDelivery ? "Delivery" : "Pickup"}
    </Badge>
  );
}

export default function FoodOrders() {
  const { business, loading: businessLoading } = useBusiness();
  const { orders, loading: ordersLoading, refetch, updateOrder } = useFoodOrders();
  const { toast } = useToast();

  const [tab, setTab] = useState<"active" | "past">("active");
  const [status, setStatus] = useState<
    "all" | "new" | "preparing" | "ready" | "out_for_delivery" | "delivered" | "not_delivered" | "cancelled"
  >("all");
  const [riders, setRiders] = useState<Array<{ id: string; name: string; phone: string | null; portal_id: string | null; portal_enabled: boolean }>>([]);
  const [assigning, setAssigning] = useState<Record<string, string>>({}); // orderId -> staffId

  const currency = (business as any)?.currency || "GHS";
  const bizId = (business as any)?.id || "";

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!bizId) return;
      try {
        const { data, error } = await (supabase as any)
          .from("staff")
          .select("id,name,phone,portal_id,portal_enabled,active")
          .eq("business_id", bizId)
          .eq("active", true)
          .order("name");
        if (error) throw error;
        if (!mounted) return;
        setRiders((data || []).map((r: any) => ({
          id: String(r.id),
          name: String(r.name),
          phone: r.phone ? String(r.phone) : null,
          portal_id: r.portal_id ? String(r.portal_id) : null,
          portal_enabled: !!r.portal_enabled,
        })));
      } catch {
        // ignore
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [bizId]);

  const isPastStatus = (s: string) => ["delivered", "cancelled", "not_delivered"].includes(String(s || ""));

  const activeOrders = useMemo(() => {
    return (orders || []).filter((o: any) => !isPastStatus(String(o.status || "new")));
  }, [orders]);

  const pastOrders = useMemo(() => {
    return (orders || []).filter((o: any) => isPastStatus(String(o.status || "new")));
  }, [orders]);

  const filtered = useMemo(() => {
    const base = tab === "past" ? pastOrders : activeOrders;
    return base.filter((o: any) => {
      if (status !== "all" && String(o.status || "new") !== status) return false;
      return true;
    });
  }, [activeOrders, pastOrders, status, tab]);

  const isLoading = businessLoading || ordersLoading;

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader title="Orders" description="Food orders from your public ordering page" />

        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setTab("active")}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex-1 sm:flex-none ${
                tab === "active" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground/80"
              }`}
            >
              Active ({activeOrders.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("past")}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex-1 sm:flex-none ${
                tab === "past" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground/80"
              }`}
            >
              Past ({pastOrders.length})
            </button>
          </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          className="flex h-10 w-full sm:w-[180px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Filter status"
        >
          <option value="all">All</option>
          <option value="new">New</option>
          <option value="preparing">Preparing</option>
          <option value="ready">Ready</option>
          <option value="out_for_delivery">Out for delivery</option>
          <option value="delivered">Delivered</option>
          <option value="not_delivered">Not delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <Button variant="outline" onClick={() => refetch()} disabled={ordersLoading}>
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          No orders found.
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((o: any) => (
            <Card key={o.id} className="border border-border">
              <CardContent className="p-5 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <div className="font-semibold text-foreground text-base">
                        {o.confirmation_code ? `#${o.confirmation_code}` : "Order"}
                      </div>
                      <FulfillmentChip isDelivery={!!o.delivery_address || !!(o as any)?.delivery_place_id} />
                      <StatusChip status={String(o.status || "new")} />
                    </div>

                    <div className="mt-3 rounded-xl border border-border bg-muted/20 p-3 sm:p-4 space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer</div>
                      <div className="grid gap-1.5 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-foreground font-medium truncate">{o.employee_name || "Customer"}</span>
                        </div>
                        {o.customer_phone ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{formatPhoneForDisplay(String(o.customer_phone))}</span>
                          </div>
                        ) : null}
                        {o.customer_email ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{o.customer_email}</span>
                          </div>
                        ) : null}
                      </div>

                      {o.delivery_address ? (
                        <div className="pt-2 border-t border-border/60">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery</div>
                          <div className="mt-1 flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0 text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <span className="line-clamp-2">{o.delivery_address}</span>
                            </div>
                            {(() => {
                              const url = mapsUrl({
                                placeId: (o as any)?.delivery_place_id,
                                address: (o as any)?.delivery_address,
                                lat: (o as any)?.delivery_lat,
                                lng: (o as any)?.delivery_lng,
                              });
                              if (!url) return null;
                              return (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline flex-shrink-0"
                                  title="Open in Google Maps"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  Maps
                                </a>
                              );
                            })()}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="w-full lg:w-[220px]">
                    <div className="text-xs font-medium text-muted-foreground mb-1">Update status</div>
                    {(() => {
                      const isFinal = isPastStatus(String(o.status || ""));
                      const currentStatus = String(o.status || "new");
                      return (
                        <select
                          value={currentStatus}
                          disabled={isFinal}
                          onChange={async (e) => {
                            const v = String(e.target.value);
                            try {
                              const isDelivery = !!o.delivery_address || !!(o as any)?.delivery_place_id;
                              const isFinalNow = isPastStatus(String(o.status || ""));
                              if (isFinalNow) return;
                              if (v === "out_for_delivery" && isDelivery) {
                                const selectedStaffId = assigning[o.id] || String((o as any)?.assigned_staff_id || "");
                                if (!selectedStaffId) {
                                  toast({
                                    title: "Select a delivery rider",
                                    description: "Assign a rider before setting Out for delivery.",
                                    variant: "destructive",
                                  });
                                  return;
                                }
                                await updateOrder(o.id, {
                                  status: v,
                                  assigned_staff_id: selectedStaffId,
                                  assigned_at: new Date().toISOString(),
                                });
                              } else {
                                await updateOrder(o.id, { status: v });
                              }
                              toast({ title: "Status updated" });

                              void (async () => {
                                try {
                                  void sendOwnerPush({
                                    businessId: business?.id,
                                    title: "Food order status changed",
                                    body: `Order ${o.confirmation_code ? `#${o.confirmation_code} ` : ""}is now ${v.replaceAll("_", " ")}.`,
                                    url: "/app/orders",
                                    data: {
                                      context: "food_order_status_changed",
                                      context_id: o.id,
                                      new_status: v,
                                    },
                                  });

                                  await supabase.functions.invoke("send-email", {
                                    body: { type: "food_order_status_changed", orderId: o.id, newStatus: v },
                                  });
                                } catch {
                                  // ignore
                                }
                              })();
                            } catch (err: any) {
                              toast({
                                title: "Failed to update",
                                description: err?.message || "Try again.",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Update status"
                        >
                          <option value="new">New</option>
                          <option value="preparing">Preparing</option>
                          <option value="ready">Ready</option>
                          <option value="out_for_delivery">Out for delivery</option>
                          <option value="delivered">Delivered</option>
                          <option value="not_delivered">Not delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      );
                    })()}

                    {/* Assign rider (only useful for delivery orders) */}
                    {!!o.delivery_address || !!(o as any)?.delivery_place_id ? (
                      <div className="mt-3">
                        <div className="text-xs font-medium text-muted-foreground mb-1">Assign rider</div>
                        <select
                          value={String(assigning[o.id] || (o as any)?.assigned_staff_id || "")}
                          disabled={isPastStatus(String(o.status || ""))}
                          onChange={(e) => {
                            const staffId = String(e.target.value);
                            setAssigning((prev) => ({ ...prev, [o.id]: staffId }));
                          }}
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Assign rider"
                        >
                          <option value="" disabled>
                            Select a rider
                          </option>
                          {(riders || []).map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                              {r.portal_enabled && r.portal_id ? "" : " (no portal)"}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Set status to <span className="font-medium">Out for delivery</span> to notify the rider and customer.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                  <div className="text-sm font-semibold text-foreground">Items</div>
                  {(o.items || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No line items (legacy order).</div>
                  ) : (
                    <div className="space-y-2">
                      {(o.items || []).map((it: any) => (
                        <div key={it.id} className="flex items-start justify-between gap-4 text-sm">
                          <div className="min-w-0">
                            <div className="font-medium text-foreground">{it.item_name}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              Qty: {Number(it.quantity || 1)} • Unit:{" "}
                              {money(
                                o.currency || currency,
                                Number(it.unit_price || 0) + addonsTotalPerUnit(it),
                              )}
                            </div>
                            {Array.isArray((it as any)?.addons) && (it as any).addons.length > 0 ? (
                              <div className="mt-1 space-y-0.5">
                                {(it as any).addons.map((a: any, idx: number) => (
                                  <div key={String(a?.id || idx)} className="text-xs text-muted-foreground">
                                    + {String(a?.name || "Add-on")}{" "}
                                    {Math.max(1, Number(a?.quantity || 1))}×{" "}
                                    {money(o.currency || currency, Number(a?.price_amount || 0))}
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {it.notes ? <div className="text-xs text-muted-foreground mt-0.5">Note: {it.notes}</div> : null}
                          </div>
                          <div className="font-semibold text-foreground whitespace-nowrap">
                            {money(
                              o.currency || currency,
                              (Number(it.unit_price || 0) + addonsTotalPerUnit(it)) * Number(it.quantity || 1),
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {o.notes ? (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Order notes:</span> {o.notes}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}

